import { Directive, ElementRef, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';

/** movement past this many pixels counts as a drag rather than a click */
const DRAG_THRESHOLD = 4;

/**
 * Click-and-drag horizontal scrolling for a native overflow-x strip, so a
 * desktop mouse can pull a row sideways the way the category chips already can
 * (those use ngx-drag-scroll, which replaces the element; this only listens on
 * whatever is there, so `.ui-hscroll` keeps its own layout and its native
 * touch scrolling).
 *
 * Usage: <div class="ui-hscroll" uiDragScroll>…</div>
 *
 * Mouse only — touch and pen fall through to the browser's own scrolling. A
 * drag past the threshold swallows the click that follows it, so pulling the
 * row does not open the card the pointer happens to be resting on.
 */
@Directive({
  selector: '[uiDragScroll]',
})
export class UiDragScrollDirective implements OnInit, OnDestroy {
  private pointerDown = false;
  private dragged = false;
  private startX = 0;
  private startScrollLeft = 0;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    const node = this.el.nativeElement;

    // none of this needs change detection — scrollLeft is set directly
    this.zone.runOutsideAngular(() => {
      node.addEventListener('pointerenter', this.onPointerEnter);
      node.addEventListener('pointerdown', this.onPointerDown);
      node.addEventListener('dragstart', this.onDragStart);
      node.addEventListener('click', this.onClickCapture, true);
      window.addEventListener('pointermove', this.onPointerMove, {
        passive: false,
      });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    });
  }

  ngOnDestroy(): void {
    const node = this.el.nativeElement;
    node.removeEventListener('pointerenter', this.onPointerEnter);
    node.removeEventListener('pointerdown', this.onPointerDown);
    node.removeEventListener('dragstart', this.onDragStart);
    node.removeEventListener('click', this.onClickCapture, true);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }

  private get scrollable(): boolean {
    const node = this.el.nativeElement;
    return node.scrollWidth - node.clientWidth > 1;
  }

  /** only offer the grab cursor when there is actually something to pull */
  private onPointerEnter = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse') return;
    if (this.scrollable) {
      this.renderer.setStyle(this.el.nativeElement, 'cursor', 'grab');
    } else {
      this.renderer.removeStyle(this.el.nativeElement, 'cursor');
    }
  };

  private onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!this.scrollable) return;
    // a field in the strip (tip amount, promo code) keeps native text selection
    if ((event.target as HTMLElement)?.closest?.('input, textarea, select')) return;

    this.pointerDown = true;
    this.dragged = false;
    this.startX = event.clientX;
    this.startScrollLeft = this.el.nativeElement.scrollLeft;
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.pointerDown) return;

    const node = this.el.nativeElement;
    const dx = event.clientX - this.startX;

    if (!this.dragged) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      this.dragged = true;
      this.renderer.setStyle(node, 'cursor', 'grabbing');
      this.renderer.setStyle(node, 'user-select', 'none');
    }

    node.scrollLeft = this.startScrollLeft - dx;
    event.preventDefault();
  };

  private onPointerUp = (): void => {
    if (!this.pointerDown) return;
    this.pointerDown = false;

    if (!this.dragged) return;

    const node = this.el.nativeElement;
    this.renderer.setStyle(node, 'cursor', 'grab');
    this.renderer.removeStyle(node, 'user-select');
    // `dragged` stays true so onClickCapture can swallow the click this mouseup
    // is about to produce; a pointerup outside the strip leaves it set, and the
    // next pointerdown clears it before any click can be affected.
  };

  /** an <img> inside the strip would otherwise start a native image drag */
  private onDragStart = (event: Event): void => {
    if (this.pointerDown) event.preventDefault();
  };

  private onClickCapture = (event: Event): void => {
    if (!this.dragged) return;
    this.dragged = false;
    event.stopPropagation();
    event.preventDefault();
  };
}
